import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { toast } from "react-toastify";
import Button from "../ui/Button";
import Modal from "../ui/Modal";
import { isValidCNP, isValidPhone } from "../../utils/validation";
import DropdownSelect from "../ui/DropdownSelect";

const DURATION_OPTIONS = [
  { label: "30 min", value: 0.5 },
  { label: "1 oră", value: 1 },
  { label: "1 oră 30 min", value: 1.5 },
  { label: "2 ore", value: 2 },
];

const RECALL_OPTIONS = [
  { label: "Niciun recall", value: "none" },
  { label: "6 luni", value: "6m" },
  { label: "1 an", value: "1y" },
];
const MAX_RADIOGRAPH_FILE_BYTES = 5 * 1024 * 1024;
const inputClasses =
  "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white transition focus:outline-none focus:ring-0 focus:border-gray-300";
const textareaClasses =
  "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white transition focus:outline-none focus:ring-0 focus:border-gray-300 min-h-[120px] resize-y";
const labelClasses = "text-sm font-medium text-gray-700";

const toFormDataFromPatient = (patient, overrides = {}) => ({
  lastName: patient?.lastName || "",
  firstName: patient?.firstName || "",
  phone: patient?.phone || "",
  email: typeof patient?.email === "string" ? patient.email : "",
  cnp: typeof patient?.cnp === "string" ? patient.cnp : "",
  city: typeof patient?.city === "string" ? patient.city : "",
  notes: overrides.notes ?? "",
  isMinor: !!patient?.isMinor,
  legalRepresentative: {
    lastName: patient?.legalRepresentative?.lastName || "",
    firstName: patient?.legalRepresentative?.firstName || "",
    city: patient?.legalRepresentative?.city || "",
    relation: patient?.legalRepresentative?.relation || "",
  },
  image: patient?.image || null,
  radiographs: [],
});

export default function AppointmentFormModal({
  selectedSlot,
  formData,
  setFormData,
  selectedPatientId,
  setSelectedPatientId,
  recall,
  setRecall,
  duration,
  setDuration,
  pacienti,
  currentUser,
  selectedCategory = "",
  setSelectedCategory = () => {},
  categoryOptions = [],
  categoryColors = {},
  onSave,
  onCancelAppointment,
  onRescheduleAppointment,
  actionNote,
  setActionNote,
  lockPatient = false,
  onCancel,
}) {
  const fileInputRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [showRepresentativeModal, setShowRepresentativeModal] = useState(false);
  const [representativeDraft, setRepresentativeDraft] = useState({
    lastName: "",
    firstName: "",
    city: "",
    relation: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [patientSearchFocused, setPatientSearchFocused] = useState(false);
  const [representativeSource, setRepresentativeSource] = useState(null);

  const isExistingSelection = Boolean(selectedPatientId);
  const patientLocked = Boolean(lockPatient);

  const ensureRepresentativeContainer = (prev) =>
    prev.legalRepresentative &&
    (prev.legalRepresentative.lastName ||
      prev.legalRepresentative.firstName ||
      prev.legalRepresentative.city ||
      prev.legalRepresentative.relation)
      ? prev.legalRepresentative
      : { lastName: "", firstName: "", city: "", relation: "" };

  const allowedPatients = useMemo(() => {
    const list = Array.isArray(pacienti) ? pacienti : [];
    if (currentUser?.role === "admin") return list;
    const userEmail = (currentUser?.email || "").toLowerCase();
    return list.filter((patient) => (patient.medicEmail || "").toLowerCase() === userEmail);
  }, [pacienti, currentUser]);

  const filteredPatients = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const pool = Array.from(allowedPatients);
    if (!term) return pool.slice(0, 8);
    return pool
      .filter((patient) => {
        const fullName = `${patient.lastName || ""} ${patient.firstName || ""}`.toLowerCase();
        const cnp = (patient.cnp || "").toString().toLowerCase();
        const phone = (patient.phone || "").toString().toLowerCase();
        const email = (patient.email || "").toString().toLowerCase();
        const code = (patient.code || "").toString().toLowerCase();
        return (
          fullName.includes(term) ||
          cnp.includes(term) ||
          phone.includes(term) ||
          email.includes(term) ||
          code.includes(term)
        );
      })
      .slice(0, 8);
  }, [allowedPatients, searchTerm]);

  const findExactPatient = useCallback(
    (term) => {
      const normalized = (term || "").trim().toLowerCase();
      if (!normalized) return null;
      const matches = allowedPatients.filter((patient) => {
        const fullName = `${patient.lastName || ""} ${patient.firstName || ""}`.trim().toLowerCase();
        const code = (patient.code || "").toString().toLowerCase();
        const email = (patient.email || "").toString().toLowerCase();
        const phone = (patient.phone || "").toString().toLowerCase();
        const cnp = (patient.cnp || "").toString().toLowerCase();
        return (
          fullName === normalized ||
          code === normalized ||
          email === normalized ||
          phone === normalized ||
          cnp === normalized
        );
      });
      if (matches.length === 1) return matches[0];
      return null;
    },
    [allowedPatients]
  );

  const resolvePatientId = useCallback(
    (patient) => {
      const directId = patient?.id || patient?._id;
      if (directId) return directId;
      const lastName = (patient?.lastName || "").trim().toLowerCase();
      const firstName = (patient?.firstName || "").trim().toLowerCase();
      const phone = (patient?.phone || "").toString().trim().toLowerCase();
      const email = (patient?.email || "").toString().trim().toLowerCase();
      const cnp = (patient?.cnp || "").toString().trim().toLowerCase();
      const code = (patient?.code || "").toString().trim().toLowerCase();
      if (!lastName && !firstName && !phone && !email && !cnp && !code) return "";
      const matches = allowedPatients.filter((p) => {
        const fullName = `${p.lastName || ""} ${p.firstName || ""}`.trim().toLowerCase();
        const nameMatch =
          (lastName || firstName) && fullName === `${lastName} ${firstName}`.trim();
        return (
          (code && (p.code || "").toString().toLowerCase() === code) ||
          (email && (p.email || "").toString().toLowerCase() === email) ||
          (phone && (p.phone || "").toString().toLowerCase() === phone) ||
          (cnp && (p.cnp || "").toString().toLowerCase() === cnp) ||
          nameMatch
        );
      });
      if (matches.length === 1) return matches[0].id || matches[0]._id || "";
      return "";
    },
    [allowedPatients]
  );

  const hasRepresentative = useMemo(() => {
    const rep = formData.legalRepresentative || {};
    return [rep.lastName, rep.firstName, rep.city, rep.relation].some((value) =>
      typeof value === "string" && value.trim().length > 0
    );
  }, [formData.legalRepresentative]);

  const handleRepresentativeButtonClick = () => {
    if (isExistingSelection) return;
    openRepresentativeModal(formData.isMinor ? "minor" : "manual");
  };

  useEffect(() => {
    if (!isExistingSelection) return;
    const existing = allowedPatients.find((p) => p.id === selectedPatientId);
    if (existing) {
      setSearchTerm(`${existing.lastName || ""} ${existing.firstName || ""}`.trim());
    }
  }, [allowedPatients, isExistingSelection, selectedPatientId]);

  useEffect(() => {
    if (selectedPatientId) return;
    if (patientSearchFocused) return;
    const match = findExactPatient(searchTerm);
    if (!match) return;
    setSelectedPatientId(match.id);
    setSearchTerm(`${match.lastName || ""} ${match.firstName || ""}`.trim());
  }, [findExactPatient, patientSearchFocused, searchTerm, selectedPatientId, setSelectedPatientId]);

  useEffect(() => {
    if (!selectedSlot) {
      setSearchTerm("");
    }
  }, [selectedSlot]);

  if (!selectedSlot) return null;

  // Upload radiografie
  const handleAddRadiograph = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_RADIOGRAPH_FILE_BYTES) {
      toast.error("Radiografia trebuie să aibă maximum 5MB.");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const rxId = `rx-${Date.now()}`;
      const result = typeof reader.result === "string" ? reader.result : "";
      setFormData((prev) => ({
        ...prev,
        image: result || prev.image,
        radiographs: result ? [{ id: rxId, url: result }] : prev.radiographs || [],
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleManualFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: false }));
  };

  const openRepresentativeModal = (source = null) => {
    const current = ensureRepresentativeContainer(formData);
    setFormData((prev) => ({
      ...prev,
      legalRepresentative: ensureRepresentativeContainer(prev),
    }));
    setRepresentativeDraft({
      lastName: current.lastName || "",
      firstName: current.firstName || "",
      city: current.city || "",
      relation: current.relation || "",
    });
    setRepresentativeSource(source);
    setShowRepresentativeModal(true);
  };

  const handleMinorToggle = (checked) => {
    if (isExistingSelection) return;
    if (checked) {
      setFormData((prev) => ({ ...prev, isMinor: true }));
      openRepresentativeModal("minor");
    } else {
      setShowRepresentativeModal(false);
      setRepresentativeSource(null);
      setFormData((prev) => ({
        ...prev,
        isMinor: false,
      }));
    }
    setErrors((prev) => ({
      ...prev,
      isMinor: false,
      rep_lastName: false,
      rep_first_name: false,
      rep_city: false,
      rep_relation: false,
    }));
  };

  const handleRepresentativeModalCancel = () => {
    const current = formData.legalRepresentative || {};
    const hasData = Boolean(
      (current.lastName || "").trim() ||
      (current.firstName || "").trim() ||
      (current.city || "").trim() ||
      (current.relation || "").trim()
    );
    if (!hasData && representativeSource == "manual") {
      setFormData((prev) => ({
        ...prev,
        legalRepresentative: {
          lastName: "",
          firstName: "",
          city: "",
          relation: "",
        },
      }));
    }
    if (!hasData && representativeSource == "minor") {
      setFormData((prev) => ({ ...prev, isMinor: false }));
    }
    setRepresentativeSource(null);
    setShowRepresentativeModal(false);
  };

  const handleRepresentativeModalConfirm = (data) => {
    const sanitized = {
      lastName: data.lastName?.trim() || "",
      firstName: data.firstName?.trim() || "",
      city: data.city?.trim() || "",
      relation: data.relation?.trim() || "",
    };
    setFormData((prev) => ({
      ...prev,
      isMinor: representativeSource === "minor" ? true : prev.isMinor,
      legalRepresentative: sanitized,
    }));
    setRepresentativeDraft(sanitized);
    setErrors((prev) => ({
      ...prev,
      rep_lastName: false,
      rep_first_name: false,
      rep_city: false,
      rep_relation: false,
    }));
    setRepresentativeSource(null);
    setShowRepresentativeModal(false);
  };

  const handleSelectPatient = (patient) => {
    const patientId = resolvePatientId(patient);
    if (!patientId) {
      toast.error("Pacientul selectat nu are un ID valid.");
      return;
    }
    setSelectedPatientId(patientId);
    setSearchTerm(`${patient.lastName || ""} ${patient.firstName || ""}`.trim());
    setPatientSearchFocused(false);
    setErrors({});
    setFormData((prev) => ({
      ...toFormDataFromPatient(patient, { notes: prev.notes }),
      image: patient.image || prev.image || "",
    }));
  };

  const handleClearSelectedPatient = () => {
    setSelectedPatientId("");
    setSearchTerm("");
    setFormData((prev) => ({
      ...prev,
      lastName: "",
      firstName: "",
      phone: "",
      email: "",
      cnp: "",
      city: "",
      isMinor: false,
      legalRepresentative: {
        lastName: "",
        firstName: "",
        city: "",
        relation: "",
      },
      image: prev.image || "",
      radiographs: [],
    }));
  };

  // ✅ Validare actualizată – CNP, email și oraș complet opționale
  const resolveExistingSelection = () => {
    if (isExistingSelection) {
      return allowedPatients.find((p) => p.id === selectedPatientId) || null;
    }
    return findExactPatient(searchTerm);
  };

  const validateForm = () => {
    const nextErrors = {};
    let hasError = false;

    const markError = (field, message) => {
      if (!hasError) toast.error(message);
      hasError = true;
      nextErrors[field] = true;
    };

    if (!isExistingSelection) {
      const lastName = formData.lastName?.trim() || "";
      const firstName = formData.firstName?.trim() || "";
      const phoneSanitized = (formData.phone || "").replace(/\D+/g, "");
      const emailTrimmed = (formData.email || "").trim();
      const cnpSanitized = (formData.cnp || "").replace(/\D+/g, "");
      const rep = formData.legalRepresentative || {};
      const nameRegex = /^[A-Za-zĂÂÎȘȚăâîșț\-'\s]{2,}$/u;

      // câmpuri obligatorii
      if (!lastName || !nameRegex.test(lastName))
        markError("lastName", "Introdu un nume valid.");
      if (!firstName || !nameRegex.test(firstName))
        markError("firstName", "Introdu un prenume valid.");
      if (!isValidPhone(phoneSanitized))
        markError("phone", "Telefon invalid (doar cifre).");

      // Email și CNP opționale, dar validate dacă sunt completate
      if (cnpSanitized && !isValidCNP(cnpSanitized))
        markError("cnp", "CNP invalid (13 cifre).");
      if (emailTrimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed))
        markError("email", "Email invalid.");

      if (formData.isMinor) {
        if (!rep.lastName || !nameRegex.test(rep.lastName))
          markError("rep_lastName", "Nume reprezentant invalid.");
        if (!rep.firstName || !nameRegex.test(rep.firstName))
          markError("rep_firstName", "Prenume reprezentant invalid.");
        if (!rep.relation)
          markError("rep_relation", "Calitate reprezentant lipsă.");
        if (!rep.city)
          markError("rep_city", "Oraș reprezentant lipsă.");
      }
    }

    setErrors(nextErrors);
    return !hasError;
  };
  return (
    <Modal
      title={
        selectedSlot.rescheduleFromId
          ? "Reprogramează programare"
          : selectedSlot.appointmentId
            ? "Editează programare"
            : "Programare nouă"
      }
      onClose={onCancel}
      size="md"
      showClose={true}
    >
      <div className="space-y-4 pr-1 sm:pr-0 pb-24" style={{ paddingBottom: "calc(6rem + 0.75rem)" }}>
        {/* data și ora slotului */}
        <h4 className="font-medium text-sm text-gray-700">
          {selectedSlot.day}{" "}
          {String(Math.floor(selectedSlot.hour)).padStart(2, "0")}:
          {selectedSlot.hour % 1 === 0 ? "00" : "30"}
        </h4>

        {/* căutare pacient existent */}
        <div className="space-y-2">
          <label className={labelClasses} htmlFor="patient-search">
            Caută pacient existent
          </label>
          <div className="relative">
            <input
              id="patient-search"
              type="search"
              placeholder="Nume, prenume, CNP, telefon sau email"
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setSearchTerm(value);
                if (isExistingSelection) {
                  setSelectedPatientId("");
                }
              }}
              onFocus={() => setPatientSearchFocused(true)}
              onBlur={() => setTimeout(() => setPatientSearchFocused(false), 120)}
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              className={inputClasses}
              disabled={patientLocked}
            />
            {isExistingSelection && !patientLocked ? (
              <button
                type="button"
                onClick={handleClearSelectedPatient}
                className="absolute right-2 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-full bg-blue-100 text-xs font-semibold text-blue-700 shadow hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-300"
                aria-label="Șterge pacientul selectat"
              >
                ✕
              </button>
            ) : null}
          </div>
          {patientLocked && (
            <p className="text-xs text-gray-500">
              Reprogramare: pacientul nu poate fi schimbat.
            </p>
          )}
          {!isExistingSelection && !patientLocked && patientSearchFocused && filteredPatients.length > 0 && (
            <ul className="border border-gray-200 rounded bg-white max-h-48 overflow-y-auto text-sm">
              {filteredPatients.map((patient, index) => (
                <li
                  key={
                    patient.id ||
                    patient.code ||
                    patient.email ||
                    `${patient.lastName || "patient"}-${patient.firstName || "unknown"}-${index}`
                  }
                >
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-blue-100 flex flex-col"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleSelectPatient(patient)}
                  >
                    <span className="font-medium">
                      {patient.lastName} {patient.firstName}
                    </span>
                    <span className="text-xs text-gray-600">
                      {(patient.phone || "-") + " • " + (patient.email || "fără email")}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* câmpuri pacient */}
        <div className="mt-4">
          <label className={clsx(labelClasses, "block mb-2")}>
            {isExistingSelection ? "Pacient selectat" : "Pacient nou"}
          </label>
          <input
            type="text"
            placeholder="Nume"
            value={formData.lastName}
            onChange={(e) => handleManualFieldChange("lastName", e.target.value)}
            className={clsx(
              inputClasses,
              "mb-2",
              errors.lastName && "border-red-500 focus:border-red-500"
            )}
            disabled={isExistingSelection || patientLocked}
          />
          <input
            type="text"
            placeholder="Prenume"
            value={formData.firstName}
            onChange={(e) => handleManualFieldChange("firstName", e.target.value)}
            className={clsx(
              inputClasses,
              "mb-2",
              errors.firstName && "border-red-500 focus:border-red-500"
            )}
            disabled={isExistingSelection || patientLocked}
          />
          <input
            type="text"
            placeholder="Telefon"
            value={formData.phone}
            onChange={(e) => handleManualFieldChange("phone", e.target.value)}
            className={clsx(
              inputClasses,
              "mb-2",
              errors.phone && "border-red-500 focus:border-red-500"
            )}
            disabled={isExistingSelection || patientLocked}
          />
          <input
            type="email"
            placeholder="Email (opțional)"
            value={formData.email}
            onChange={(e) => handleManualFieldChange("email", e.target.value)}
            className={clsx(
              inputClasses,
              "mb-2",
              errors.email && "border-red-500 focus:border-red-500"
            )}
            disabled={isExistingSelection || patientLocked}
          />
          <input
            type="text"
            placeholder="CNP (opțional)"
            value={formData.cnp}
            onChange={(e) => handleManualFieldChange("cnp", e.target.value)}
            maxLength={13}
            className={clsx(
              inputClasses,
              "mb-2",
              errors.cnp && "border-red-500 focus:border-red-500"
            )}
            disabled={isExistingSelection || patientLocked}
          />
          <input
            type="text"
            placeholder="Oraș (opțional)"
            value={formData.city}
            onChange={(e) => handleManualFieldChange("city", e.target.value)}
            className={clsx(inputClasses, "mb-2")}
            disabled={isExistingSelection || patientLocked}
          />
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <label htmlFor="isMinor" className="flex items-center gap-2 text-sm font-medium">
              <input
                id="isMinor"
                type="checkbox"
                checked={!!formData.isMinor}
                onChange={(e) => handleMinorToggle(e.target.checked)}
                disabled={isExistingSelection || patientLocked}
              />
              Pacient minor
            </label>
            <div className="ml-auto">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={handleRepresentativeButtonClick}
                disabled={isExistingSelection || patientLocked}
              >
                {hasRepresentative ? "Editează reprezentant legal" : "Adaugă reprezentant legal"}
              </Button>
            </div>
          </div>
        </div>

        {/* observații cu toggle */}
        <div className="flex flex-col gap-2">
          <Button
            variant={formData.showNotes ? "secondary" : "primary"}
            onClick={() =>
              setFormData((prev) => ({ ...prev, showNotes: !prev.showNotes }))
            }
          >
            📝 {formData.showNotes ? "Ascunde observații" : "Adaugă observații"}
          </Button>

          {formData.showNotes && (
            <textarea
              value={formData.notes || ""}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className={clsx(textareaClasses, "mb-2")}
              placeholder="Adaugă observații despre programare..."
              rows={3}
            />
          )}
        </div>

        {/* radiografii */}
        <div className="flex flex-col gap-2">
          <h4 className="font-semibold text-sm">Radiografie</h4>
          <div className="flex flex-wrap gap-2">
            {(formData.radiographs || []).map((rx, index) => (
              <div
                key={rx.id || rx.url || `rx-${index}`}
                className="h-16 w-20 bg-gray-100 rounded flex items-center justify-center overflow-hidden"
              >
                <img src={rx.url} alt="Radiografie" className="object-cover h-full w-full" />
              </div>
            ))}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleAddRadiograph}
          />
          <Button variant="primary" onClick={() => fileInputRef.current?.click()}>
            ➕ Încarcă radiografie
          </Button>
        </div>

        <div className="space-y-6">
          <div className="flex flex-col gap-1">
            <label className={labelClasses}>Categorie</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className={inputClasses}
            >
              <option value="">Fără categorie</option>
              {categoryOptions.map((categoryName) => (
                <option key={categoryName} value={categoryName}>
                  {categoryName}
                </option>
              ))}
            </select>
            {selectedCategory && categoryColors?.[selectedCategory] ? (
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <span
                  className="inline-block h-3 w-3 rounded-full border border-gray-200"
                  style={{ backgroundColor: categoryColors[selectedCategory] }}
                />
                <span>Culoare categorie: {categoryColors[selectedCategory]}</span>
              </div>
            ) : null}
          </div>
          <div className="flex flex-col gap-1">
            <label className={labelClasses}>Durată</label>
            <DropdownSelect
              options={DURATION_OPTIONS}
              value={duration}
              onChange={(nextValue) => setDuration(Number(nextValue))}
              ariaLabel="Durata programării"
            />
          </div>
          {currentUser?.role === "admin" && (
            <div className="flex flex-col gap-1">
              <label className={labelClasses}>Recall</label>
              <DropdownSelect
                options={RECALL_OPTIONS}
                value={recall}
                onChange={setRecall}
                ariaLabel="Intervalul de recall"
              />
            </div>
          )}
        </div>

      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button
          variant="primary"
          onClick={() => {
            const resolvedExisting = resolveExistingSelection();
            const resolvedId = resolvePatientId(resolvedExisting);
            if (resolvedId) {
              if (!selectedPatientId) {
                setSelectedPatientId(resolvedId);
                setFormData((prev) =>
                  toFormDataFromPatient(resolvedExisting, { notes: prev.notes })
                );
              }
              onSave({ resolvedPatientId: resolvedId });
              return;
            }
            if (validateForm()) onSave();
          }}
        >
          {selectedSlot.rescheduleFromId
            ? "🔁 Reprogramează"
            : selectedSlot.appointmentId
              ? "💾 Salvează"
              : "📌 Programează"}
        </Button>
        {selectedSlot.appointmentId && (
          <>
            <Button
              variant="warning"
              onClick={() => onRescheduleAppointment?.(selectedSlot, actionNote)}
            >
              🔁 Reprogramează
            </Button>
            <Button
              variant="danger"
              onClick={() => onCancelAppointment?.(selectedSlot, actionNote)}
            >
              ✖️ Anulează
            </Button>
          </>
        )}
        {!selectedSlot.appointmentId && !selectedSlot.rescheduleFromId && (
          <Button variant="secondary" onClick={onCancel}>
            Anulează
          </Button>
        )}
      </div>

      {(selectedSlot.appointmentId || selectedSlot.rescheduleFromId) && (
        <div className="mt-4">
          <label className={labelClasses}>Motiv anulare / reprogramare (opțional)</label>
          <textarea
            value={actionNote || ""}
            onChange={(e) => setActionNote?.(e.target.value)}
            className={textareaClasses}
            placeholder="Scrie o notiță pentru anulare sau reprogramare..."
            rows={3}
          />
        </div>
      )}

      {showRepresentativeModal && (
        <LegalRepresentativeModal
          initialData={representativeDraft}
          onConfirm={handleRepresentativeModalConfirm}
          onCancel={handleRepresentativeModalCancel}
        />
      )}
    </Modal>
  );
}

/* ✅ Componenta LegalRepresentativeModal */
function LegalRepresentativeModal({ initialData, onConfirm, onCancel }) {
  const [draft, setDraft] = useState({
    lastName: initialData.lastName || "",
    firstName: initialData.firstName || "",
    city: initialData.city || "",
    relation: initialData.relation || "",
  });

  useEffect(() => {
    setDraft({
      lastName: initialData.lastName || "",
      firstName: initialData.firstName || "",
      city: initialData.city || "",
      relation: initialData.relation || "",
    });
  }, [initialData]);

  const handleChange = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const nameRegex = /^[A-Za-zĂÂÎȘȚăâîșț\-'\s]{2,}$/u;
    const lastName = draft.lastName.trim();
    const firstName = draft.firstName.trim();
    const city = draft.city.trim();
    const relation = draft.relation.trim();

    if (!nameRegex.test(lastName)) {
      toast.error("Introdu un nume valid pentru reprezentant.");
      return;
    }
    if (!nameRegex.test(firstName)) {
      toast.error("Introdu un prenume valid pentru reprezentant.");
      return;
    }
    if (!city) {
      toast.error("Completează orașul reprezentantului legal.");
      return;
    }
    if (!relation) {
      toast.error("Specifică calitatea reprezentantului legal.");
      return;
    }

    onConfirm({ lastName, firstName, city, relation });
  };

  return (
    <Modal title="Reprezentant legal" onClose={onCancel} size="sm">
      <div className="p-4 space-y-2">
        <input
          type="text"
          placeholder="Nume"
          value={draft.lastName}
          onChange={(e) => handleChange("lastName", e.target.value)}
          className={inputClasses}
        />
        <input
          type="text"
          placeholder="Prenume"
          value={draft.firstName}
          onChange={(e) => handleChange("firstName", e.target.value)}
          className={inputClasses}
        />
        <input
          type="text"
          placeholder="Oraș"
          value={draft.city}
          onChange={(e) => handleChange("city", e.target.value)}
          className={inputClasses}
        />
        <input
          type="text"
          placeholder="Calitate"
          value={draft.relation}
          onChange={(e) => handleChange("relation", e.target.value)}
          className={inputClasses}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onCancel}>
            Renunță
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Continuă
          </Button>
        </div>
      </div>
    </Modal>
  );
}
